/**
 * Per-site notification switches on a User Details site card (Related Companies tab).
 *
 * Each switch writes one UserSitePermission.notify_* flag for this user at this
 * company. Procurement and Quality are read by the emit pipeline via
 * DOMAIN_NOTIFY_FLAGS; Documents (notify_compliance) is read only by the
 * document notices — it gates supplier uploads and Read & Accept requests, not
 * questionnaires, ratings or scorecards. Turning one off silences those
 * notifications for this user at this company, bell and email alike. It is
 * deliberately not labelled "emails": it used to say that while nothing read
 * the flag at all. Onboarding, RFQ and system events are not listed because
 * they always deliver — account and platform business is not module noise.
 *
 * CR-6457: Weekly Arrears Report (notify_weekly_arrears) is an email only, and
 * independent of Procurement.
 */

import type { UserSitePermission } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { Bell, Mail } from 'lucide-react';

interface SiteNotificationSwitchesProps {
  perms: UserSitePermission;
  /** Company type of the user being edited; Documents is customer-only. */
  companyType: string | undefined;
  isSaving: boolean;
  onToggle: (permission: string, value: boolean) => void;
}

export function SiteNotificationSwitches({
  perms, companyType, isSaving, onToggle,
}: SiteNotificationSwitchesProps) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Notifications
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Turning one off stops both the in-app bell and the email for this
        company only.
      </p>
      <div className="mt-2 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Procurement
          </span>
          <Switch
            checked={perms.notify_procurement}
            onCheckedChange={(checked) => onToggle('notify_procurement', checked)}
            disabled={isSaving}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Quality
          </span>
          <Switch
            checked={perms.notify_quality}
            onCheckedChange={(checked) => onToggle('notify_quality', checked)}
            disabled={isSaving}
          />
        </div>
        {/* Documents gates customer-side recipients only; hidden for suppliers. */}
        {companyType === 'customer' && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Documents
              </span>
              <Switch
                checked={perms.notify_compliance} aria-label="Documents notifications"
                onCheckedChange={(checked) => onToggle('notify_compliance', checked)}
                disabled={isSaving}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Supplier document uploads and Read &amp; Accept requests
            </p>
          </>
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Weekly Arrears Report
          </span>
          <Switch
            checked={perms.notify_weekly_arrears} aria-label="Weekly Arrears Report email"
            onCheckedChange={(checked) => onToggle('notify_weekly_arrears', checked)}
            disabled={isSaving}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Email only. Sent when the organisation turns the report on in Admin Setup.
        </p>
      </div>
    </div>
  );
}
